// DEV-08 批次作業對話框：改名（規則）、更新韌體、套設定（以選取的其中一台為範本，複製到其他同型號設備；上方「常用 / 進階 / 技術」篩選晶片，預設常用 + 進階）、
// 指派位置（ORG-04：team 組織家戶的設備 / 通道，一個家戶一次請求、同一交易）、重新開機（POST /v1/me/devices/commands，伺服器逐台驗權）。
// 逐台列結果，失敗的可只重試失敗的。可以跨來源選設備：沒權限的那幾台先列出原因（照「我的設備」的 actions / can_manage），不執行、不中斷其他台
// （SPEC #device-permissions-ui；改名、韌體、位置、重開機是管理動作 → can_manage；套設定 → device.control）。
// 瀏覽器執行：關掉對話框 / 頁面就停在那裡（已經送出的不會收回）。
import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useConfirm } from '../../components/ConfirmDialog';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { KinioError, kinioFetch, planeBase, setDesired } from '@kinio/web-sdk';
import { Dialog } from '../../components/Dialog';
import { can, canManage, type VmsDevice } from '../../lib/devices';
import { whyNot } from '../../lib/denial';
import { batchCommand } from '../../lib/myDevices';
import { locationApi, subgApi } from '../../lib/subg';
import { orgApi, type Site } from '../../lib/planeApi';
import { siteRows } from '../../lib/siteTree';
import { applyHomeResult, groupByHome } from './locationBatch';
import { deviceApi, type SettingItem } from '../../lib/deviceApi';
import { copyChanges, flatItems, mismatches, stillPending, toState } from './copyPlan';
import { batchSummary, precheck, renameByRule, retryFailed, runBatch, type BatchOutcome, type BatchRow } from './batch';
import { TierChips, defaultTierSet } from './TierChips';
import type { Tier } from './settingsTiers';
import { useScope } from '../../lib/scope';

type Mode = 'rename' | 'firmware' | 'settings' | 'location' | 'reboot';
const MODES: Mode[] = ['rename', 'firmware', 'settings', 'location', 'reboot'];

const READBACK_TRIES = 12;
const READBACK_MS = 2000;
const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

export function BatchDialog({ devices, close }: { devices: VmsDevice[]; close(): void }) {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const { ask, dialog } = useConfirm();
  const [mode, setMode] = useState<Mode>('rename');
  /** 這台在這個模式做不了的原因（null = 可以）。 */
  // 改名、韌體、重開機是維運 device.maintain（node_admin / 維運員也有，SPEC #device-permissions-layers）；指派位置才是管理動作 can_manage
  const reasonOf = (d: VmsDevice): string | null =>
    mode === 'settings'
      ? whyNot(t, d.access, 'device.control', i18n.language)
      : mode === 'location'
        ? canManage(d) ? null : t('batch.no_manage')
        : canManage(d) || can(d, 'device.maintain') ? null : t('batch.no_maintain');
  const [rule, setRule] = useState('{name}');
  const [start, setStart] = useState(1);
  const [rows, setRows] = useState<BatchRow<VmsDevice>[] | null>(null);
  const [busy, setBusy] = useState(false);
  const rowsRef = useRef<BatchRow<VmsDevice>[]>([]);
  const [srcKey, setSrcKey] = useState(devices[0]?.key ?? '');
  const src = devices.find((d) => d.key === srcKey) ?? devices[0];
  const srcItems = useRef<{ key: string; items: Promise<SettingItem[]> } | null>(null);
  const [tiers, setTiers] = useState<Set<Tier>>(defaultTierSet);
  // 技術晶片：選取的設備我都能管理（家戶 owner / admin）才給
  const { homes } = useScope();
  const canTech = devices.every((d) => canManage(d));
  // 指派位置：選取的設備都要在同一個 team 組織的家戶、我都能管理（看不到 / 管不到的逐台列原因）
  const locHomes = devices.map((d) => homes.find((h) => h.home_id === d.homeId));
  const locOrg =
    locHomes.length > 0 && locHomes.every((h) => h?.org_kind === 'team' && h.org_id && h.org_id === locHomes[0]?.org_id) ? String(locHomes[0]!.org_id) : '';
  const sites = useQuery({
    queryKey: ['sites', locOrg],
    queryFn: () => orgApi.sites({ api: devices[0].apiEndpoint, org_id: locOrg }),
    enabled: mode === 'location' && !!locOrg,
    retry: false,
  });
  const siteOpts = useMemo(() => siteRows((sites.data ?? []) as Site[]).filter((r) => r.site.status === 'active'), [sites.data]);
  const [site, setSite] = useState('');
  // 韌體只對有連線的設備本體（通道跟著 parent）
  const targets = useMemo(
    () =>
      mode === 'firmware' || mode === 'reboot'
        ? devices.filter((d) => !d.parentAid)
        : mode === 'settings'
          ? devices.filter((d) => d.key !== srcKey)
          : mode === 'location' && !locOrg
            ? []
            : devices,
    [devices, mode, srcKey, locOrg],
  );
  const index = useMemo(() => new Map(targets.map((d, i) => [d.key, i])), [targets]);
  // 逐台預先判權：沒權限的列出原因、不執行
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const initial = useMemo(() => precheck(targets, reasonOf), [targets, mode, i18n.language]);
  const deniedN = initial.filter((r) => r.state === 'denied').length;

  const act = async (d: VmsDevice): Promise<BatchOutcome> => {
    if (mode === 'rename') {
      const name = renameByRule(rule, d.name, index.get(d.key) ?? 0, start);
      if (!name) throw Object.assign(new Error('bad name'), { code: 'BAD_NAME' });
      if (name === d.name) return { state: 'skipped', detail: t('batch.same') };
      await subgApi.rename(d.apiEndpoint, d.homeId, d.aid, name);
      return { state: 'ok', detail: name };
    }
    if (mode === 'settings') {
      // 範本那台的設定只讀一次；同型號（都是通道也算）才套
      if (!!d.parentAid !== !!src.parentAid || d.model !== src.model) return { state: 'skipped', detail: t('batch.skip_model') };
      if (srcItems.current?.key !== src.key)
        srcItems.current = { key: src.key, items: deviceApi.settings(src.apiEndpoint, src.homeId, src.aid).then(flatItems) };
      const from = await srcItems.current.items;
      const changes = copyChanges(from, flatItems(await deviceApi.settings(d.apiEndpoint, d.homeId, d.aid)), undefined, tiers);
      if (!changes.length) return { state: 'skipped', detail: t('batch.skip_same') };
      await setDesired(d.apiEndpoint, d.homeId, d.aid, toState(changes), 'vms-web');
      // 等設備 ack（key 不在 desired）；之後跟寫入值不同 = 設備正規化或拒收
      let waiting = changes.map((c) => c.key);
      let bad: string[] = [];
      for (let i = 0; i < READBACK_TRIES && waiting.length; i++) {
        await sleep(READBACK_MS);
        const now = flatItems(await deviceApi.settings(d.apiEndpoint, d.homeId, d.aid));
        waiting = stillPending(changes, now);
        if (!waiting.length) bad = mismatches(changes, now);
      }
      if (waiting.length) throw Object.assign(new Error('timeout'), { code: t('dset.timeout') });
      if (bad.length) throw Object.assign(new Error('not applied'), { code: t('chcopy.not_applied', { list: bad.join(', ') }) });
      return { state: 'ok', detail: t('chcopy.applied_n', { n: changes.length }) };
    }
    const base = `${planeBase(d.apiEndpoint)}/v1/homes/${encodeURIComponent(d.homeId)}/things/${encodeURIComponent(d.aid)}/ota`;
    const st = await kinioFetch<{ update_available?: boolean; release?: { version?: string } }>(base);
    if (!st?.update_available) return { state: 'skipped', detail: t('ddev.fw_latest') };
    const r = await kinioFetch<{ delivery?: string }>(base + '/update', { method: 'POST' });
    return { state: 'ok', detail: r?.delivery === 'app' ? t('ddev.fw_via_app') : `→ ${st.release?.version ?? ''}` };
  };

  // 指派位置：每個家戶先讀每台目前的 location_version（沒有 = 0），再一次送出；結果攤回每一列
  const runLocation = async (from: BatchRow<VmsDevice>[]) => {
    const todo = from.filter((r) => r.state === 'pending');
    for (const r of todo) r.state = 'running';
    setRows([...from]);
    const siteName = siteOpts.find((o) => o.site.site_id === site)?.path ?? site;
    for (const [homeId, group] of groupByHome(todo.map((r) => r.item))) {
      try {
        const items = await Promise.all(
          group.map(async (d) => {
            const cur = await locationApi.get(d.apiEndpoint, homeId, d.aid).catch((e) => {
              if (e instanceof KinioError && e.status === 404) return { location_version: 0 };
              throw e;
            });
            return { device_ref: d.aid, site_id: site, expected_version: cur?.location_version ?? 0 };
          }),
        );
        await locationApi.assign(group[0].apiEndpoint, homeId, items);
        applyHomeResult(from, group, { ok: true, siteName });
      } catch (e) {
        const idx = e instanceof KinioError ? e.detail?.index : undefined;
        applyHomeResult(from, group, { ok: false, code: String((e as { code?: string }).code ?? (e as Error).message ?? e), index: typeof idx === 'number' ? idx : undefined });
      }
      setRows([...from]);
    }
  };

  // 重新開機：跨來源一次送（每個 plane 一個請求），伺服器逐台驗權，結果攤回每一列
  const runReboot = async (from: BatchRow<VmsDevice>[]) => {
    const todo = from.filter((r) => r.state === 'pending');
    for (const r of todo) r.state = 'running';
    setRows([...from]);
    const byApi = new Map<string, BatchRow<VmsDevice>[]>();
    for (const r of todo) byApi.set(r.item.apiEndpoint, [...(byApi.get(r.item.apiEndpoint) ?? []), r]);
    for (const [api, group] of byApi) {
      try {
        const res = await batchCommand(
          api,
          group.map((r) => ({ home_id: r.item.homeId, device_ref: r.item.aid })),
          { op: 'set', id: 'kinio_system.reboot', wait_seconds: 8 },
        );
        for (const r of group) {
          const x = res.find((y) => y.home_id === r.item.homeId && y.device_ref === r.item.aid);
          if (x?.status === 'ok') {
            r.state = 'ok';
            r.detail = t('dctl.sent');
          } else {
            r.state = 'failed';
            r.detail = x?.error?.error ?? 'UPSTREAM';
          }
        }
      } catch (e) {
        for (const r of group) {
          r.state = 'failed';
          r.detail = String((e as { code?: string }).code ?? (e as Error).message ?? e);
        }
      }
      setRows([...from]);
    }
  };

  const run = async (from: BatchRow<VmsDevice>[]) => {
    if (mode === 'reboot' && !rows && !(await ask({ title: t('batch.reboot_confirm', { count: from.filter((r) => r.state === 'pending').length }), action: t('common.action_reboot') }))) return;
    if (mode === 'firmware' && !rows && !(await ask({ title: t('batch.fw_confirm', { count: from.length }), action: t('common.action_update_fw') }))) return;
    if (mode === 'settings' && !rows && !(await ask({ title: t('batch.settings_confirm', { count: from.length, name: src.name }), action: t('common.action_apply') }))) return;
    if (mode === 'settings' && !rows) srcItems.current = null;
    setBusy(true);
    rowsRef.current = from;
    setRows([...from]);
    if (mode === 'location') await runLocation(from);
    else if (mode === 'reboot') await runReboot(from);
    else await runBatch(from, act, (r) => setRows([...r]));
    setBusy(false);
    void qc.invalidateQueries();
  };
  const sum = rows ? batchSummary(rows) : null;

  return (
    <Dialog open title={t('batch.title', { count: devices.length })} onClose={close} wide>
      <div className="form" data-testid="batch-dialog">
        <div className="seg" role="tablist">
          {MODES.map((m) => (
            <button
              key={m}
              role="tab"
              aria-pressed={mode === m}
              disabled={busy}
              onClick={() => {
                setMode(m);
                setRows(null);
              }}
            >
              {t(`batch.mode_${m}`)}
            </button>
          ))}
        </div>
        {mode === 'rename' ? (
          <>
            <div className="toolbar">
              <label className="field" style={{ flex: 1 }}>
                <span>{t('batch.rule')}</span>
                <input className="input" value={rule} onChange={(e) => setRule(e.target.value)} disabled={busy} aria-label={t('batch.rule')} />
              </label>
              <label className="field">
                <span>{t('batch.start')}</span>
                <input
                  className="input"
                  type="number"
                  min={0}
                  style={{ width: 80 }}
                  value={start}
                  onChange={(e) => setStart(Number(e.target.value) || 0)}
                  disabled={busy}
                  aria-label={t('batch.start')}
                />
              </label>
            </div>
            <p className="muted small">{t('batch.rule_help')}</p>
          </>
        ) : mode === 'settings' ? (
          <>
            <label className="field">
              <span>{t('batch.settings_source')}</span>
              <select
                className="input"
                value={src?.key ?? ''}
                onChange={(e) => (setSrcKey(e.target.value), setRows(null))}
                disabled={busy}
                aria-label={t('batch.settings_source')}
              >
                {devices.map((d) => (
                  <option key={d.key} value={d.key}>
                    {d.name}
                    {d.model ? ` · ${d.model}` : ''}
                  </option>
                ))}
              </select>
            </label>
            <div className="row-inline">
              <span className="muted small">{t('tier.filter')}</span>
              <TierChips value={tiers} onChange={(v) => (setTiers(v), setRows(null))} canTech={canTech} />
            </div>
            <p className="muted small">{t('batch.settings_help')}</p>
          </>
        ) : mode === 'location' ? (
          locOrg ? (
            <>
              <label className="field">
                <span>{t('batch.loc_site')}</span>
                <select className="input" value={site} onChange={(e) => (setSite(e.target.value), setRows(null))} disabled={busy} aria-label={t('batch.loc_site')}>
                  <option value="">{t('batch.loc_pick')}</option>
                  {siteOpts.map((o) => (
                    <option key={o.site.site_id} value={o.site.site_id}>
                      {'\u00a0\u00a0'.repeat(o.depth)}
                      {o.site.name}
                    </option>
                  ))}
                </select>
              </label>
              <p className="muted small">{t('batch.loc_help')}</p>
            </>
          ) : (
            <p className="muted small" data-testid="batch-loc-unavailable">
              {t('batch.loc_need_org')}
            </p>
          )
        ) : mode === 'reboot' ? (
          <p className="muted small">{t('batch.reboot_help', { n: targets.length })}</p>
        ) : (
          <p className="muted small">{t('batch.fw_help', { count: targets.length })}</p>
        )}
        <div className="table-wrap" style={{ maxHeight: 320, overflow: 'auto' }}>
          <table className="grid">
            <thead>
              <tr>
                <th>{t('devices.col_name')}</th>
                <th>{mode === 'rename' ? t('batch.col_new') : mode === 'settings' ? t('devices.col_model') : mode === 'location' ? t('devices.col_home') : t('devices.col_fw')}</th>
                <th>{t('batch.col_result')}</th>
              </tr>
            </thead>
            <tbody>
              {(rows ?? initial).map((r, i) => (
                <tr key={r.item.key} data-batch-row={r.item.aid} data-state={rows ? r.state : r.state === 'denied' ? 'denied' : 'idle'}>
                  <td>{r.item.name}</td>
                  <td className="mono">
                    {mode === 'rename'
                      ? (renameByRule(rule, r.item.name, index.get(r.item.key) ?? i, start) ?? '—')
                      : mode === 'settings'
                        ? r.item.model || '—'
                        : mode === 'location'
                          ? r.item.homeName
                          : r.item.firmware || '—'}
                  </td>
                  <td>
                    {r.state === 'denied' ? (
                      <span className="muted" data-testid="batch-denied">
                        {t('batch.denied', { why: r.detail ?? '' })}
                      </span>
                    ) : !rows ? (
                      '—'
                    ) : r.state === 'failed' ? (
                      <span className="error">{t(`batch.err_${r.detail}`, { defaultValue: t('common.op_failed', { code: r.detail }) })}</span>
                    ) : r.state === 'ok' ? (
                      `✓ ${r.detail ?? ''}`
                    ) : r.state === 'skipped' ? (
                      r.detail
                    ) : (
                      t(`batch.st_${r.state}`)
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {sum && (
          <p className="small" data-testid="batch-summary">
            {t('batch.summary', { ok: sum.ok, skipped: sum.skipped, failed: sum.failed, left: sum.pending + sum.running })}
            {sum.denied > 0 && ' ' + t('batch.denied_n', { count: sum.denied })}
          </p>
        )}
        {!rows && deniedN > 0 && (
          <p className="small muted" data-testid="batch-denied-note">
            {t('batch.denied_n', { count: deniedN })}
          </p>
        )}
        <div className="toolbar">
          {!rows && (
            <button
              className="btn btn-primary"
              disabled={busy || targets.length - deniedN === 0 || (mode === 'location' && !site)}
              onClick={() => void run(initial.map((r) => ({ ...r })))}
            >
              {t('batch.run', { n: targets.length - deniedN })}
            </button>
          )}
          {rows && sum!.failed > 0 && (
            <button className="btn btn-primary" disabled={busy} onClick={() => void run(retryFailed(rowsRef.current))}>
              {t('chcopy.retry', { n: sum!.failed })}
            </button>
          )}
          <button className="btn" onClick={close}>
            {t('common.close')}
          </button>
        </div>
      </div>
      {dialog}
    </Dialog>
  );
}
